import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Monitor,
  Server,
  Laptop,
  Smartphone,
  Building2,
  Calendar,
  Shield,
  Tag,
  Cpu,
  HardDrive,
  Network,
  User,
  MapPin,
  Loader2,
  AlertCircle,
  Hash
} from 'lucide-react';
import { configurationsAPI } from '../services/api';

export default function ConfigurationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['configuration', id],
    queryFn: () => configurationsAPI.get(id),
    enabled: !!id,
  });

  const config = data?.data?.configuration;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getIcon = (type) => {
    const typeName = type?.name?.toLowerCase() || '';
    if (typeName.includes('server')) return Server;
    if (typeName.includes('laptop')) return Laptop;
    if (typeName.includes('mobile') || typeName.includes('phone')) return Smartphone;
    return Monitor;
  };

  const BackButton = () => (
    <button
      onClick={() => {
        const returnUrl = sessionStorage.getItem('configurationsReturnUrl') || '/assets';
        sessionStorage.removeItem('configurationsReturnUrl');
        navigate(returnUrl, { state: { refresh: Date.now() } });
      }}
      className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-6"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to Assets
    </button>
  );

  if (isLoading) {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
            <p className="text-neutral-500 dark:text-neutral-400">Loading asset...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="animate-fade-in">
        <BackButton />
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">Asset Not Found</h2>
          <p className="text-red-700 dark:text-red-300">
            {error?.response?.data?.error || 'This asset could not be loaded.'}
          </p>
        </div>
      </div>
    );
  }

  const Icon = getIcon(config.type);
  const isActive = config.status?.name === 'Active';

  // Check warranty status
  const warrantyDate = config.warrantyExpirationDate ? new Date(config.warrantyExpirationDate) : null;
  const isWarrantyExpired = warrantyDate && warrantyDate < new Date();
  const isWarrantyExpiringSoon = warrantyDate && !isWarrantyExpired &&
    warrantyDate < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000); // 90 days

  return (
    <div className="animate-fade-in max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <BackButton />
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
              <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{config.name}</h1>
              <p className="text-neutral-500 dark:text-neutral-400">
                {config.type?.name || 'Asset'} #{config.id}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            isActive
              ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
              : 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300'
          }`}>
            {config.status?.name || 'Unknown'}
          </span>
        </div>
      </div>

      {/* Warranty Alert */}
      {warrantyDate && (isWarrantyExpired || isWarrantyExpiringSoon) && (
        <div className={`mb-6 p-4 rounded-lg border ${
          isWarrantyExpired
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-center gap-3">
            <Shield className={`w-5 h-5 ${isWarrantyExpired ? 'text-red-500' : 'text-yellow-500'}`} />
            <div>
              <span className={`font-medium ${
                isWarrantyExpired
                  ? 'text-red-900 dark:text-red-200'
                  : 'text-yellow-900 dark:text-yellow-200'
              }`}>
                {isWarrantyExpired ? 'Warranty Expired' : 'Warranty Expiring Soon'}
              </span>
              <span className={`ml-2 ${
                isWarrantyExpired
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-yellow-700 dark:text-yellow-300'
              }`}>
                ({formatDate(config.warrantyExpirationDate)})
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Asset Details */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Asset Details</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Company</div>
              <div className="font-medium text-neutral-900 dark:text-white">{config.company?.name || 'N/A'}</div>
            </div>
          </div>
          {config.site && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Site/Location</div>
                <div className="font-medium text-neutral-900 dark:text-white">{config.site?.name || 'N/A'}</div>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Type</div>
              <div className="font-medium text-neutral-900 dark:text-white">{config.type?.name || 'N/A'}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Hash className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Serial Number</div>
              <div className="font-medium font-mono text-neutral-900 dark:text-white">{config.serialNumber || 'N/A'}</div>
            </div>
          </div>
          {config.modelNumber && (
            <div className="flex items-start gap-3">
              <Monitor className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Model Number</div>
                <div className="font-medium text-neutral-900 dark:text-white">{config.modelNumber}</div>
              </div>
            </div>
          )}
          {config.tagNumber && (
            <div className="flex items-start gap-3">
              <Tag className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Asset Tag</div>
                <div className="font-medium font-mono text-neutral-900 dark:text-white">{config.tagNumber}</div>
              </div>
            </div>
          )}
          {config.manufacturer?.name && (
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Manufacturer</div>
                <div className="font-medium text-neutral-900 dark:text-white">{config.manufacturer.name}</div>
              </div>
            </div>
          )}
          {config.contact && (
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Assigned To</div>
                <div className="font-medium text-neutral-900 dark:text-white">{config.contact.name}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dates & Warranty */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Dates & Warranty</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {config.purchaseDate && (
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Purchase Date</div>
                <div className="font-medium text-neutral-900 dark:text-white">{formatDate(config.purchaseDate)}</div>
              </div>
            </div>
          )}
          {config.installDate && (
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Install Date</div>
                <div className="font-medium text-neutral-900 dark:text-white">{formatDate(config.installDate)}</div>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-neutral-400 mt-0.5" />
            <div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">Warranty Expiration</div>
              <div className={`font-medium ${
                isWarrantyExpired
                  ? 'text-red-600 dark:text-red-400'
                  : isWarrantyExpiringSoon
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-neutral-900 dark:text-white'
              }`}>
                {formatDate(config.warrantyExpirationDate)}
              </div>
            </div>
          </div>
          {config.lastLoginName && (
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-neutral-400 mt-0.5" />
              <div>
                <div className="text-sm text-neutral-500 dark:text-neutral-400">Last Login</div>
                <div className="font-medium text-neutral-900 dark:text-white">{config.lastLoginName}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Technical Specifications */}
      {(config.osType || config.osInfo || config.cpuSpeed || config.ram || config.localHardDrives || config.ipAddress || config.macAddress) && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Technical Specifications</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {(config.osType || config.osInfo) && (
              <div className="flex items-start gap-3">
                <Monitor className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">Operating System</div>
                  <div className="font-medium text-neutral-900 dark:text-white">
                    {config.osType || ''} {config.osInfo || ''}
                  </div>
                </div>
              </div>
            )}
            {config.cpuSpeed && (
              <div className="flex items-start gap-3">
                <Cpu className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">CPU Speed</div>
                  <div className="font-medium text-neutral-900 dark:text-white">{config.cpuSpeed}</div>
                </div>
              </div>
            )}
            {config.ram && (
              <div className="flex items-start gap-3">
                <HardDrive className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">RAM</div>
                  <div className="font-medium text-neutral-900 dark:text-white">{config.ram}</div>
                </div>
              </div>
            )}
            {config.localHardDrives && (
              <div className="flex items-start gap-3">
                <HardDrive className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">Storage</div>
                  <div className="font-medium text-neutral-900 dark:text-white">{config.localHardDrives}</div>
                </div>
              </div>
            )}
            {config.ipAddress && (
              <div className="flex items-start gap-3">
                <Network className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">IP Address</div>
                  <div className="font-medium font-mono text-neutral-900 dark:text-white">{config.ipAddress}</div>
                </div>
              </div>
            )}
            {config.macAddress && (
              <div className="flex items-start gap-3">
                <Network className="w-5 h-5 text-neutral-400 mt-0.5" />
                <div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400">MAC Address</div>
                  <div className="font-medium font-mono text-neutral-900 dark:text-white">{config.macAddress}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      {config.notes && (
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Notes</h2>
          <p className="text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">{config.notes}</p>
        </div>
      )}
    </div>
  );
}
